(() => {
  'use strict';

  const desktopQuery = window.matchMedia('(min-width: 750px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function onMediaChange(query, callback) {
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', callback);
    } else if (typeof query.addListener === 'function') {
      query.addListener(callback);
    }
  }

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function initializeScrollVideo(root) {
    if (root.dataset.cxtScrollVideoInitialized === 'true') return;

    const stage = root.querySelector('[data-cxt-scroll-video-stage]');
    const frame = root.querySelector('[data-cxt-scroll-video-frame]');
    const videos = Array.from(root.querySelectorAll('[data-cxt-scroll-video-element]'));
    const toggle = root.querySelector('[data-cxt-scroll-video-toggle]');

    if (!stage || !frame) return;

    root.dataset.cxtScrollVideoInitialized = 'true';

    let isNearViewport = false;
    let isUserPaused = false;
    let framePending = false;

    function getActiveVideo() {
      const desiredType = desktopQuery.matches ? 'desktop' : 'mobile';
      return videos.find((video) => video.dataset.cxtScrollVideoElement === desiredType)
        || videos[0]
        || null;
    }

    function shouldUseScrollExpansion() {
      return desktopQuery.matches && !reducedMotionQuery.matches;
    }

    function isPaused(video) {
      return !video || video.paused || isUserPaused;
    }

    function updateToggle() {
      if (!toggle) return;

      const paused = isPaused(getActiveVideo());
      toggle.setAttribute('aria-pressed', String(paused));
      toggle.setAttribute('aria-label', paused ? 'Play background video' : 'Pause background video');

      const label = toggle.querySelector('.cxt-elite-scroll-video__visually-hidden');
      if (label) label.textContent = paused ? 'Play background video' : 'Pause background video';
    }

    function loadVideo(video) {
      if (!video || video.dataset.cxtScrollVideoLoaded === 'true') return;

      const sources = Array.from(video.querySelectorAll('source[data-src]'));
      if (!sources.length) return;

      sources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });

      video.dataset.cxtScrollVideoLoaded = 'true';
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.load();
    }

    function playActiveVideo() {
      const video = getActiveVideo();
      if (!video || !isNearViewport || isUserPaused || reducedMotionQuery.matches) {
        updateToggle();
        return;
      }

      loadVideo(video);
      const playback = video.play();
      if (playback && typeof playback.catch === 'function') {
        playback.catch(() => {
          // Browsers can still refuse autoplay in exceptional low-power or data-saving modes.
          // The visible Play control remains available without affecting the section layout.
          updateToggle();
        });
      }
      updateToggle();
    }

    function pauseAllVideos() {
      videos.forEach((video) => video.pause());
      updateToggle();
    }

    function setFrameState(progress) {
      const startScale = 0.6;
      const scale = startScale + ((1 - startScale) * progress);

      root.style.setProperty('--cxt-elite-scroll-video-progress', progress.toFixed(4));
      root.style.setProperty('--cxt-elite-scroll-video-scale', scale.toFixed(4));
    }

    function updateFrame() {
      framePending = false;

      if (!shouldUseScrollExpansion()) {
        root.classList.remove('is-scroll-enhanced');
        setFrameState(1);
        return;
      }

      root.classList.add('is-scroll-enhanced');
      const scrollDistance = Math.max(root.offsetHeight - stage.offsetHeight, 1);
      const sectionTop = root.getBoundingClientRect().top;
      const progress = clamp((-sectionTop) / scrollDistance, 0, 1);
      setFrameState(progress);
    }

    function scheduleFrameUpdate() {
      if (framePending) return;
      framePending = true;
      window.requestAnimationFrame(updateFrame);
    }

    videos.forEach((video) => {
      video.addEventListener('loadeddata', () => {
        video.classList.add('is-ready');
        playActiveVideo();
      }, { once: true });

      video.addEventListener('play', updateToggle);
      video.addEventListener('pause', updateToggle);
    });

    if (toggle) {
      toggle.addEventListener('click', () => {
        const video = getActiveVideo();
        if (!video) return;

        if (isPaused(video)) {
          isUserPaused = false;
          isNearViewport = true;
          loadVideo(video);
          const playback = video.play();
          if (playback && typeof playback.catch === 'function') playback.catch(updateToggle);
        } else {
          isUserPaused = true;
          video.pause();
        }

        updateToggle();
      });
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        isNearViewport = Boolean(entry && entry.isIntersecting);

        if (isNearViewport) {
          playActiveVideo();
        } else {
          pauseAllVideos();
        }
      }, {
        rootMargin: '300px 0px',
        threshold: 0.01,
      });

      observer.observe(root);
    } else {
      isNearViewport = true;
      playActiveVideo();
    }

    const syncMediaState = () => {
      const activeVideo = getActiveVideo();
      videos.forEach((video) => {
        if (video !== activeVideo) video.pause();
      });

      if (reducedMotionQuery.matches) {
        isUserPaused = false;
        pauseAllVideos();
      } else if (isNearViewport) {
        playActiveVideo();
      }

      scheduleFrameUpdate();
      updateToggle();
    };

    onMediaChange(desktopQuery, syncMediaState);
    onMediaChange(reducedMotionQuery, syncMediaState);
    window.addEventListener('scroll', scheduleFrameUpdate, { passive: true });
    window.addEventListener('resize', scheduleFrameUpdate, { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        pauseAllVideos();
      } else if (isNearViewport && !isUserPaused && !reducedMotionQuery.matches) {
        playActiveVideo();
      }
    });

    syncMediaState();
  }

  function initializeWithin(scope) {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-cxt-scroll-video]')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-cxt-scroll-video]'));
    roots.forEach(initializeScrollVideo);
  }

  function initialize() {
    initializeWithin(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeWithin(event.target);
  });
})();
